import React, { useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import toast from "react-hot-toast";
import {
  Button,
  Checkbox,
  Divider,
  HStack,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { api } from "@/api";
import type { OrderDetails, OrderExtraCosts } from "@/store/types/order";
import { OrderPurchaseType } from "@/store/types/order";
import { OrderSummaryTable } from "./OrderSummaryTable";
import { renderInnerHtml } from "@/helpers/dom";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { AppLink } from "@/routing/AppLink";
import { H3 } from "@/components/Heading";
import { FormSummaryItem } from "@/components/Form/FormSummaryItem";
import { orderReviewAmount } from "@/constants/order";
import { IndigenousMailToLink } from "@/components/Link";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { useEffectOnce } from "usehooks-ts";
import { getApiErrorMessage } from "@/api/getApiErrorMessage";
import { useCaptchaToken } from "@/components/Captcha/useCaptchaToken";
import { telemetry } from "@/telemetry";

export const OrderSummaryPanel = observer(() => {
  const {
    activeLanguage,
    isEmergentFlow,
    isIndigenousFlow,
    orderData,
    orderTotals,
    setOrderData,
    setOrderCreatedData,
    ga4Store,
    telemetryStore,
    billingProvinceLabel,
    shippingProvinceLabel,
    organizationTypeLabel,
  } = useStore();
  const [extraCosts, setExtraCosts] = useState<OrderExtraCosts | null>(null);
  const [confirmDetails, setConfirmDetails] = useState(false);
  const language = translations.pages["order-summary"][activeLanguage];
  const indigenousLanguage = translations.common.indigenous[activeLanguage];
  const isAccountFlow = isEmergentFlow || isIndigenousFlow;

  const navigate = useAppNavigate();

  const getCaptchaToken = useCaptchaToken("create_order");

  const shippingDetails = useMemo(
    () => ({
      products: orderData.products,
      ada: orderData.ada,
      shippingBusinessName: orderData.shippingBusinessName,
      shippingCity: orderData.shippingCity,
      shippingOptionalUnit: orderData.shippingOptionalUnit,
      shippingPostalCode: orderData.shippingPostalCode,
      shippingProvince: orderData.shippingProvince,
      shippingStreetAddress: orderData.shippingStreetAddress,
    }),
    [orderData],
  );

  const costsTimer = useRef(telemetry.startTimer());

  const apiGetCosts = useApiMutate({
    mutationFn: () => api.getExtraCosts(shippingDetails),
    onMutate: () => {
      costsTimer.current = telemetry.startTimer();
    },
    onSuccess: (data) => {
      setOrderData({
        ...orderData,
        shippingCost: data.shippingCost,
        tax: data.tax,
      });
      setExtraCosts(data);
    },
    onError: () => setExtraCosts(null),
    onSettled: (data, error) =>
      telemetry.trackEvent("extra_costs_loaded", {
        result: error ? "error" : "ok",
        shipping_cost: data?.shippingCost,
        tax: data?.tax,
        duration_ms: costsTimer.current(),
      }),
  });

  useEffectOnce(() => apiGetCosts.mutate({}));

  const apiPayByCC = useApiMutate({
    mutationFn: async (data: OrderDetails) => {
      const captchaToken = await getCaptchaToken();
      return api.createOrder({ ...data, captchaToken });
    },
    onSuccess: (data) => {
      telemetryStore.orderCreated(data.orderId, OrderPurchaseType.Credit);
      setOrderCreatedData(data);
      navigate({
        to: "/order/payment",
      });
    },
    onError: (data) => {
      telemetryStore.orderCreateFailed(data, OrderPurchaseType.Credit);
      toast.error(getApiErrorMessage(data));
    },
  });

  const apiPayByPo = useApiMutate({
    mutationFn: async (data: OrderDetails) => {
      const captchaToken = await getCaptchaToken();
      return api.createOrder({ ...data, captchaToken });
    },
    onSuccess: (data) => {
      telemetryStore.orderCreated(
        data.orderId,
        OrderPurchaseType.PurchaseOrder,
      );
      setOrderCreatedData(data);
      ga4Store.purchase(data.orderId);

      const isOver45k = orderTotals.total >= orderReviewAmount;

      navigate({
        to: isOver45k ? "/order-request" : "/order-confirm",
      });
    },
    onError: (data) => {
      telemetryStore.orderCreateFailed(data, OrderPurchaseType.PurchaseOrder);
      toast.error(getApiErrorMessage(data));
    },
  });

  const handlePayByCC = () => {
    telemetryStore.paymentMethodChosen(OrderPurchaseType.Credit);
    ga4Store.beginCheckout();
    apiPayByCC.mutate({
      ...orderData,
      purchaseType: OrderPurchaseType.Credit,
    });

    setOrderData({
      ...orderData,
      purchaseType: OrderPurchaseType.Credit,
    });
  };

  const handlePayByPo = () => {
    telemetryStore.paymentMethodChosen(OrderPurchaseType.PurchaseOrder);
    ga4Store.beginCheckout();
    apiPayByPo.mutate({
      ...orderData,
      purchaseType: OrderPurchaseType.PurchaseOrder,
    });

    setOrderData({
      ...orderData,
      purchaseType: OrderPurchaseType.PurchaseOrder,
    });
  };

  const handlePayByAccount = () => {
    telemetryStore.paymentMethodChosen(OrderPurchaseType.Account);
    ga4Store.beginCheckout();
    setOrderData({
      ...orderData,
      purchaseType: OrderPurchaseType.Account,
    });
    navigate({
      to: "/order/payment",
    });
  };

  const handleEditShipping = () =>
    telemetry.trackEvent("edit_shipping_clicked");

  const editShippingDetailsLink = isIndigenousFlow ? null : (
    <AppLink
      to="/order/shipping"
      onClick={handleEditShipping}
      state={{
        jumpTo: "form",
      }}
    >
      {renderInnerHtml(language.edit)}
    </AppLink>
  );

  if (!extraCosts)
    return (
      apiGetCosts.isError && (
        <Text color="primary-light">
          An error has occurred retrieving the shipping costs.
        </Text>
      )
    );

  return (
    <Stack spacing="14">
      <Stack spacing="8">
        <HStack justifyContent="space-between">
          <H3> {renderInnerHtml(language.order_summary)}</H3>
          <AppLink
            to="/order/details"
            state={{
              jumpTo: "form",
            }}
          >
            {language.edit}
          </AppLink>
        </HStack>

        <OrderSummaryTable />
      </Stack>

      {orderData.ada && isIndigenousFlow && (
        <>
          <HStack alignItems="center">
            <H3>{language.emergent_ada}</H3>
            <Text>{orderData.ada}</Text>
          </HStack>
        </>
      )}

      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing="6"
      >
        <Stack spacing="8">
          <HStack justifyContent="space-between">
            <H3>{language.billing_address}</H3>
            {editShippingDetailsLink}
          </HStack>
          <Stack spacing="4">
            {!isIndigenousFlow && (
              <>
                <FormSummaryItem
                  label={language.name}
                  value={orderData.billingName}
                />
                <FormSummaryItem
                  label={language.business_name}
                  value={orderData.billingBusinessName}
                />
              </>
            )}
            <FormSummaryItem
              label={language.street_address}
              value={orderData.billingStreetAddress}
            />
            <FormSummaryItem
              label={language.unit_suit}
              value={orderData.billingOptionalUnit}
            />
            <FormSummaryItem
              label={language.city}
              value={orderData.billingCity}
            />
            <FormSummaryItem
              label={
                isEmergentFlow ? language.province_territory : language.province
              }
              value={billingProvinceLabel}
            />
            <FormSummaryItem
              label={language.postal_code}
              value={orderData.billingPostalCode}
            />
          </Stack>
        </Stack>
        <Stack spacing="8">
          <HStack justifyContent="space-between">
            <H3>{language.shipping_address}</H3>
            {editShippingDetailsLink}
          </HStack>
          <Stack spacing="4">
            {!isIndigenousFlow && (
              <>
                <FormSummaryItem
                  label={language.shipping_name}
                  value={orderData.shippingName}
                />
                <FormSummaryItem
                  label={language.shipping_business_name}
                  value={orderData.shippingBusinessName}
                />
              </>
            )}

            <FormSummaryItem
              label={language.shipping_street_address}
              value={orderData.shippingStreetAddress}
            />
            <FormSummaryItem
              label={language.shipping_unit_suit}
              value={orderData.shippingOptionalUnit}
            />
            <FormSummaryItem
              label={language.shipping_city}
              value={orderData.shippingCity}
            />
            <FormSummaryItem
              label={
                isEmergentFlow ? language.province_territory : language.province
              }
              value={shippingProvinceLabel}
            />
            <FormSummaryItem
              label={language.shipping_postal_code}
              value={orderData.shippingPostalCode}
            />
          </Stack>
        </Stack>
      </SimpleGrid>

      <Stack spacing="8">
        <HStack justifyContent="space-between">
          <H3>{language.contact_information}</H3>
          {editShippingDetailsLink}
        </HStack>
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing="6"
        >
          <FormSummaryItem
            label={language.contact_name}
            value={orderData.billingName}
          />
          <FormSummaryItem
            label={language.email_address}
            value={orderData.emailAddress}
          />
          <FormSummaryItem
            label={language.phone_no}
            value={orderData.phoneNumber}
          />
          {!!organizationTypeLabel && (
            <FormSummaryItem
              label={language.organization_type}
              value={organizationTypeLabel}
            />
          )}
        </SimpleGrid>
      </Stack>

      {orderData.orderReference && (
        <HStack justifyContent="space-between">
          <HStack alignItems="center">
            <H3>{language.order_reference_number}</H3>
            <Text>{orderData.orderReference}</Text>
          </HStack>
          <AppLink
            to="/order/shipping"
            onClick={handleEditShipping}
            state={{
              jumpTo: "form",
            }}
          >
            {language.edit}
          </AppLink>
        </HStack>
      )}

      {isAccountFlow && (
        <>
          <Divider />
          <HStack
            width="100%"
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={isIndigenousFlow ? "column" : "row"}
            spacing="14"
          >
            <Checkbox
              id="checkbox4"
              isChecked={confirmDetails}
              onChange={() => setConfirmDetails(!confirmDetails)}
            >
              {renderInnerHtml(language.confirm_order_details_correct)}
            </Checkbox>

            {isIndigenousFlow && (
              <Stack
                width="100%"
                borderWidth="1px"
                borderColor="white"
                borderRadius="6px"
                py="8"
                px="14"
                textAlign="center"
                spacing="8"
              >
                <Text>
                  {renderInnerHtml(language.incorrect_details_message, [
                    <IndigenousMailToLink
                      key="email"
                      subject={language.incorrect_details_email_subject}
                    >
                      <Text
                        as="span"
                        color="primary-light"
                      >
                        {indigenousLanguage.email}
                      </Text>
                    </IndigenousMailToLink>,
                  ])}
                </Text>

                <IndigenousMailToLink
                  subject={language.incorrect_details_email_subject}
                >
                  <Button alignSelf="center">
                    {renderInnerHtml(indigenousLanguage.email_button_label)}
                  </Button>
                </IndigenousMailToLink>
              </Stack>
            )}

            <Stack
              spacing="8"
              justifyContent="flex-end"
              marginLeft="auto"
            >
              <Button
                onClick={handlePayByCC}
                isDisabled={!confirmDetails}
                isLoading={apiPayByCC.isPending}
              >
                {language.pay_by_credit_card}
              </Button>

              {orderData.billingAllowPo && !isIndigenousFlow && (
                <Button
                  onClick={handlePayByPo}
                  isDisabled={!confirmDetails}
                  isLoading={apiPayByPo.isPending}
                >
                  {language.pay_by_po}
                </Button>
              )}

              {isIndigenousFlow && (
                <Button
                  onClick={handlePayByAccount}
                  isDisabled={!confirmDetails}
                  isLoading={apiPayByPo.isPending}
                >
                  {language.pay_on_account}
                </Button>
              )}
            </Stack>
          </HStack>
        </>
      )}

      {!isAccountFlow && (
        <HStack justifyContent="flex-end">
          <Button
            onClick={handlePayByCC}
            isLoading={apiPayByCC.isPending}
            whiteSpace="normal"
            wordBreak="break-word"
          >
            {renderInnerHtml(language.confirm_details_payment)}
          </Button>
        </HStack>
      )}
    </Stack>
  );
});
